import React, { useState, useEffect, useCallback } from 'react';
import {
  ShoppingBasket, Plus, Trash2, Loader2, AlertCircle,
  ChefHat, Sparkles, X, ScanLine, Wind, Clock, Flame
} from 'lucide-react';
import { pantryApi, suggestApi } from '../services/api';

const UNITS = ['g', 'kg', 'ml', 'l', 'unité', 'boîte', 'sachet', 'bouteille', 'tranche'];

const AddItemForm = ({ onAdd, onClose }) => {
  const [form, setForm] = useState({ name: '', quantity: 1, unit: 'unité', barcode: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    setErr(null);
    try {
      const item = await pantryApi.create({
        name: form.name.trim(),
        quantity: parseFloat(form.quantity) || 1,
        unit: form.unit,
        barcode: form.barcode || null
      });
      onAdd(item);
      onClose();
    } catch (error) {
      setErr(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-green-50 border border-green-200 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-800 flex items-center gap-2">
          <Plus className="w-4 h-4 text-green-600" /> Ajouter un aliment
        </h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="w-4 h-4" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input
            type="text"
            placeholder="Nom de l'aliment *"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            className="sm:col-span-2 border border-green-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-green-300 bg-white text-sm"
            disabled={saving}
          />
          <div className="flex gap-2">
            <input
              type="number"
              min="0"
              step="0.1"
              value={form.quantity}
              onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))}
              className="w-20 border border-green-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-green-300 bg-white text-sm"
              disabled={saving}
            />
            <select
              value={form.unit}
              onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
              className="flex-1 border border-green-200 rounded-xl px-2 py-2.5 focus:outline-none focus:ring-2 focus:ring-green-300 bg-white text-sm"
              disabled={saving}
            >
              {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <ScanLine className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <input
            type="text"
            placeholder="Code-barres (optionnel)"
            value={form.barcode}
            onChange={e => setForm(f => ({ ...f, barcode: e.target.value }))}
            className="flex-1 border border-gray-200 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-300 bg-white text-sm"
            disabled={saving}
          />
        </div>

        {err && (
          <p className="text-red-600 text-sm flex items-center gap-1">
            <AlertCircle className="w-4 h-4" /> {err}
          </p>
        )}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={saving || !form.name.trim()}
            className="flex items-center gap-2 bg-green-500 hover:bg-green-600 disabled:bg-green-300 text-white px-5 py-2.5 rounded-xl text-sm font-medium transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Ajouter
          </button>
          <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium transition-colors">
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
};

const SuggestionCard = ({ suggestion, airfryer = false }) => (
  <div className={`bg-white rounded-xl border p-4 shadow-sm ${airfryer ? 'border-orange-100' : 'border-orange-100'}`}>
    <div className="flex items-start justify-between gap-2 mb-2">
      <h4 className="font-semibold text-gray-800 text-sm">{suggestion.title}</h4>
      {suggestion.estimated_gl_per_serving && (
        <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium flex-shrink-0">
          CG {suggestion.estimated_gl_per_serving}
        </span>
      )}
    </div>

    {/* Airfryer stats */}
    {airfryer && (suggestion.airfryer_temp || suggestion.airfryer_time) && (
      <div className="flex gap-3 mb-2">
        {suggestion.airfryer_temp && (
          <span className="flex items-center gap-1 text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full font-medium">
            <Flame className="w-3 h-3" /> {suggestion.airfryer_temp}°C
          </span>
        )}
        {suggestion.airfryer_time && (
          <span className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
            <Clock className="w-3 h-3" /> {suggestion.airfryer_time} min
          </span>
        )}
      </div>
    )}

    {suggestion.prep_time_minutes && !airfryer && (
      <p className="text-xs text-gray-400 mb-2">⏱ {suggestion.prep_time_minutes} min</p>
    )}

    {Array.isArray(suggestion.ingredients_used) && suggestion.ingredients_used.length > 0 && (
      <div className="flex flex-wrap gap-1 mb-3">
        {suggestion.ingredients_used.map((ing, i) => (
          <span key={i} className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full">{ing}</span>
        ))}
      </div>
    )}

    {suggestion.base_instructions && (
      <p className="text-xs text-gray-600 leading-relaxed line-clamp-3">{suggestion.base_instructions}</p>
    )}

    {suggestion.health_note && (
      <p className="mt-2 text-xs text-green-600 italic">{suggestion.health_note}</p>
    )}
  </div>
);

const SuggestSection = ({
  title, description, icon: Icon, iconBg, buttonLabel, buttonClass,
  onSuggest, suggesting, error, suggestions, airfryer = false
}) => (
  <div className={`border rounded-2xl p-6 ${airfryer ? 'bg-gradient-to-br from-orange-50 to-red-50 border-orange-200' : 'bg-gradient-to-br from-orange-50 to-yellow-50 border-orange-200'}`}>
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${iconBg}`}>
          <Icon className="w-4 h-4" />
        </div>
        <h2 className="font-semibold text-gray-800">{title}</h2>
      </div>
      <button
        onClick={onSuggest}
        disabled={suggesting}
        className={`flex items-center gap-2 ${buttonClass} text-white px-4 py-2 rounded-xl text-sm font-medium transition-colors shadow-sm`}
      >
        {suggesting ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Analyse…</>
        ) : (
          <><Sparkles className="w-4 h-4" /> {buttonLabel}</>
        )}
      </button>
    </div>
    <p className="text-sm text-gray-500 mb-4">{description}</p>

    {error && (
      <div className="flex items-start gap-2 text-orange-700 text-sm bg-orange-100 rounded-xl p-3 mb-4">
        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        {error}
      </div>
    )}

    {suggestions.length > 0 && (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {suggestions.map((s, i) => (
          <SuggestionCard key={i} suggestion={s} airfryer={airfryer} />
        ))}
      </div>
    )}
  </div>
);

const PantryPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [suggestions, setSuggestions] = useState([]);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState(null);

  const [airfryerSuggestions, setAirfryerSuggestions] = useState([]);
  const [airfryerSuggesting, setAirfryerSuggesting] = useState(false);
  const [airfryerError, setAirfryerError] = useState(null);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await pantryApi.list();
      setItems(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadItems(); }, [loadItems]);

  const handleAdd = (item) => {
    setItems(prev => [...prev, item].sort((a, b) => a.name.localeCompare(b.name)));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Retirer cet article du garde-manger ?')) return;
    try {
      await pantryApi.remove(id);
      setItems(prev => prev.filter(i => i.id !== id));
    } catch (err) {
      alert('Erreur : ' + err.message);
    }
  };

  const handleSuggest = async () => {
    setSuggesting(true);
    setSuggestError(null);
    setSuggestions([]);
    try {
      const result = await suggestApi.fromPantry();
      setSuggestions(result.suggestions || []);
      if (!result.suggestions?.length) setSuggestError(result.message || 'Aucune suggestion disponible.');
    } catch (err) {
      setSuggestError(err.message);
    } finally {
      setSuggesting(false);
    }
  };

  const handleAirfryerSuggest = async () => {
    setAirfryerSuggesting(true);
    setAirfryerError(null);
    setAirfryerSuggestions([]);
    try {
      const result = await suggestApi.airfryerRoasting();
      setAirfryerSuggestions(result.suggestions || []);
      if (!result.suggestions?.length) setAirfryerError(result.message || 'Aucune suggestion disponible.');
    } catch (err) {
      setAirfryerError(err.message);
    } finally {
      setAirfryerSuggesting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <ShoppingBasket className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Mon Frigo & Garde-manger</h1>
              <p className="text-sm text-gray-500">{items.length} article{items.length !== 1 ? 's' : ''} en stock</p>
            </div>
          </div>
          <button
            onClick={() => setShowForm(v => !v)}
            className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-xl font-medium transition-colors shadow-sm text-sm"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Ajouter</span>
          </button>
        </div>
      </div>

      {showForm && (
        <AddItemForm onAdd={handleAdd} onClose={() => setShowForm(false)} />
      )}

      {/* Pantry list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-7 h-7 animate-spin text-green-400" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
          <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
          <p className="text-red-700 font-medium">{error}</p>
          <button onClick={loadItems} className="mt-3 text-sm text-red-600 underline">Réessayer</button>
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm p-10 text-center">
          <ShoppingBasket className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Garde-manger vide</p>
          <p className="text-sm text-gray-400 mt-1">Ajoutez vos aliments pour obtenir des suggestions de repas</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="divide-y divide-gray-50">
            {items.map(item => (
              <div key={item.id} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors">
                <div>
                  <span className="font-medium text-gray-800 text-sm">{item.name}</span>
                  {item.barcode && (
                    <span className="ml-2 text-xs text-gray-400">{item.barcode}</span>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-600 font-medium">
                    {item.quantity} {item.unit}
                  </span>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="text-gray-300 hover:text-red-500 transition-colors"
                    title="Retirer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI sections — only if pantry has items */}
      {items.length > 0 && (
        <>
          <SuggestSection
            title="Qu'est-ce qu'on mange ?"
            description={`L'IA analyse vos ${items.length} ingrédients et propose des recettes classiques à IG bas réalisables maintenant.`}
            icon={ChefHat}
            iconBg="bg-orange-100 text-orange-600"
            buttonLabel="Suggérer"
            buttonClass="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300"
            onSuggest={handleSuggest}
            suggesting={suggesting}
            error={suggestError}
            suggestions={suggestions}
            airfryer={false}
          />

          <SuggestSection
            title="🌀 Rôtissage Rapide Airfryer"
            description={`L'IA sélectionne les ingrédients compatibles Airfryer (protéines, légumes racines…) et propose des recettes rapides à IG bas optimisées pour la cuisson à air chaud.`}
            icon={Wind}
            iconBg="bg-orange-200 text-orange-700"
            buttonLabel="Suggestions Airfryer"
            buttonClass="bg-red-500 hover:bg-red-600 disabled:bg-red-300"
            onSuggest={handleAirfryerSuggest}
            suggesting={airfryerSuggesting}
            error={airfryerError}
            suggestions={airfryerSuggestions}
            airfryer={true}
          />
        </>
      )}
    </div>
  );
};

export default PantryPage;
